import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";
import { ScrollText, RefreshCw } from "lucide-react";

const FILTERS = [
  { v: "", label: "All activity" },
  { v: "user", label: "Members & Roles" },
  { v: "project", label: "Projects" },
  { v: "builder", label: "Builder Folders" },
  { v: "template", label: "Task Templates" },
  { v: "checklist_template", label: "Checklists" },
  { v: "settings", label: "Settings" },
  { v: "task", label: "Assignment Emails" },
];

const verbColor = (action) => {
  if (/email/.test(action)) return { bg: "#EFF6FF", color: "#2563EB" };
  if (/create|add/.test(action)) return { bg: "#ECFDF5", color: "#059669" };
  if (/delete|remove/.test(action)) return { bg: "#FFF1F2", color: "#E11D48" };
  if (/reorder/.test(action)) return { bg: "#F4F4F5", color: "#52525B" };
  return { bg: "#FFFBEB", color: "#D97706" }; // update
};

const prettyAction = (a) => a.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [entity, setEntity] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [before, setBefore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const load = useCallback(async (ent) => {
    setLoading(true);
    try {
      const r = await api.get(`/audit`, { params: { limit: 50, ...(ent ? { entity_type: ent } : {}) } });
      setEntries(r.data);
      setHasMore(r.headers["x-has-more"] === "1");
      setBefore(r.headers["x-next-before"] || null);
    } catch (e) { if (process.env.NODE_ENV === "development") console.error("Failed to load audit log:", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(entity); }, [entity, load]);

  const loadOlder = async () => {
    if (!before) return;
    setLoadingOlder(true);
    try {
      const r = await api.get(`/audit`, { params: { limit: 50, before, ...(entity ? { entity_type: entity } : {}) } });
      setEntries((p) => [...p, ...r.data]);
      setHasMore(r.headers["x-has-more"] === "1");
      setBefore(r.headers["x-next-before"] || null);
    } catch (e) { if (process.env.NODE_ENV === "development") console.error("Failed to load older audit entries:", e); }
    finally { setLoadingOlder(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1000px]" data-testid="audit-page">
      <header className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <ScrollText className="h-6 w-6 text-zinc-900" />
            <h1 className="font-heading text-4xl font-black tracking-tighter">Audit Log</h1>
          </div>
          <p className="text-zinc-500 text-sm mt-2">Who changed roles, settings, projects and folders — for accountability.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={entity} onChange={(e) => setEntity(e.target.value)}
            className="h-9 rounded-sm border border-zinc-200 bg-white px-3 text-sm" data-testid="audit-filter">
            {FILTERS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
          </select>
          <button onClick={() => load(entity)} className="h-9 w-9 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-50" title="Refresh" data-testid="audit-refresh">
            <RefreshCw className="h-4 w-4 text-zinc-500" />
          </button>
        </div>
      </header>

      {loading ? (
        <div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      ) : entries.length === 0 ? (
        <div className="border border-zinc-200 bg-white p-12 text-center">
          <p className="text-zinc-500 text-sm">No audit entries yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 divide-y divide-zinc-100">
          {entries.map((e) => {
            const c = verbColor(e.action);
            return (
              <div key={e.audit_id} className="flex items-start gap-3 px-4 py-3" data-testid={`audit-row-${e.audit_id}`}>
                <Avatar id={e.actor_id} name={e.actor_name || "?"} size={30} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-zinc-900">{e.actor_name || "Unknown"}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5" style={{ background: c.bg, color: c.color }}>
                      {prettyAction(e.action)}
                    </span>
                    <span className="text-xs text-zinc-400 ml-auto whitespace-nowrap">{new Date(e.at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-zinc-600 mt-0.5 break-words">{e.summary}</p>
                </div>
              </div>
            );
          })}
          {hasMore && (
            <div className="p-3">
              <button onClick={loadOlder} disabled={loadingOlder}
                className="text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors disabled:opacity-50"
                data-testid="audit-load-older">
                {loadingOlder ? "Loading…" : "Load older entries"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
